import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE, fetchApiJson } from "../config/apiBase";
import Seo, { mergeSeoKeywords } from "../Components/Seo";
import { useI18n } from "../i18n/I18nContext";
import { useTranslatedCopy } from "../i18n/useTranslatedCopy";
import { getServicesPageCopy } from "../content/localeCopy";
import PageTitle from "./include/PageTitle";

const fallbackBlogImage = "/img/blog/1.jpg";
const fallbackAgentImages = ["/img/team/4.jpg", "/img/team/2.jpg", "/img/team/5.jpg", "/img/team/1.jpg"];
const testimonialIconImage =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'%3E%3Crect width='160' height='160' rx='80' fill='%23f5f1df'/%3E%3Ccircle cx='80' cy='58' r='28' fill='%23bba55c'/%3E%3Cpath d='M32 132c8-28 25-42 48-42s40 14 48 42' fill='%23bba55c'/%3E%3C/svg%3E";

const testimonialItems = [
  {
    quote:
      "QTECH helped us improve operations and tenant coordination while keeping reporting clear and consistent.",
    name: "Muhammad Mustafa",
    role: "Property Owner",
    image: "img/testimonial/1.jpg",
  },
  {
    quote:
      "Their marketing support improved project visibility and generated stronger buyer interest in less time.",
    name: "Muhammad Mahmood",
    role: "Marketing Client",
    image: "img/testimonial/2.jpg",
  },
  {
    quote:
      "The brokerage process was transparent and practical from pricing to negotiation and documentation.",
    name: "Muhammad Ali",
    role: "Buyer Representative",
    image: "img/testimonial/3.jpg",
  },
  {
    quote:
      "Maintenance response and follow-up are reliable, and that makes day-to-day asset operations much easier.",
    name: "Huda Hoori",
    role: "Facility Supervisor",
    image: "img/testimonial/4.jpg",
  },
];

const aboutStaticCopy = {
  aboutUs: "About Us",
  servicesButton: "Our Services",
  trustCallout:
    "We measure our work by your outcomes: stronger asset performance, fewer surprises, and reporting you can trust.",
  trustCallout2: "That is how we build partnerships that last beyond a single transaction.",
  team: "Team",
  propertyAgents: "Property Agents",
  noAgents: "No active agents have been added yet.",
  testimonialSubtitle: "Our Testimonial",
  testimonialTitle: "Clients Feedback",
  newsSubtitle: "News & Blogs",
  newsTitle: "Latest News",
  noBlogs: "No published blog posts are available yet.",
  readMore: "Read more",
  by: "by",
  newsTag: "News",
  contactQtech: "Contact QTECH",
  heading: "Integrated real estate solutions in Saudi Arabia",
  paragraph1:
    "We are a specialized real estate services company providing integrated solutions in property management, real estate development, brokerage, marketing, and maintenance and operations. We operate in line with the highest professional standards in the Kingdom of Saudi Arabia, using a methodology focused on maximizing asset value, promoting sustainability and delivering reliable results for property owners, investors and clients.",
  paragraph2:
    "With a commitment to professionalism, transparency and customer satisfaction, we strive to build long-term partnerships and deliver real estate solutions that ensure every client journey is smooth, secure and successful. Our goal is to add real value to every property we manage and to be the trusted partner driving its growth and success.",
  coreSubtitle: "Our Services",
  coreTitle: "Our Core Services",
};

const resolveBlogImage = (url, base) => {
  if (!url) return fallbackBlogImage;
  if (/^data:image\//i.test(url)) return url;
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/uploads\//i.test(url)) {
    return `${base}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/")) return `${base}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
};

const resolveAgentImage = (url) => {
  if (!url) return "";
  if (/^data:image\//i.test(url)) return url;
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/uploads\//i.test(url)) {
    return `${API_BASE}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/")) return `${API_BASE}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
};

const formatBlogDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const About = () => {
  const { locale } = useI18n();
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [blogAssetBase, setBlogAssetBase] = useState("");
  const testimonialSliderRef = useRef(null);
  const newsSliderRef = useRef(null);

  const servicesCopy = useTranslatedCopy(getServicesPageCopy(), locale, "about-services-copy");
  const localizedBlogs = latestBlogs;
  const localizedAgents = agents;
  const localizedTestimonials = useTranslatedCopy(testimonialItems, locale, "about-testimonials");
  const localizedUi = useTranslatedCopy(aboutStaticCopy, locale, "about-ui");

  useEffect(() => {
    let ignore = false;

    const loadDynamicSections = async () => {
      try {
        const [blogsResult, agentsResult] = await Promise.all([
          fetchApiJson(`/api/blogs?limit=5&lang=${locale}`),
          fetchApiJson(`/api/agents?lang=${locale}`),
        ]);

        if (ignore) return;

        setLatestBlogs(Array.isArray(blogsResult?.json?.data) ? blogsResult.json.data.slice(0, 5) : []);
        setAgents(Array.isArray(agentsResult?.json?.data) ? agentsResult.json.data.slice(0, 6) : []);
        setBlogAssetBase(blogsResult?.base || "");
      } catch {
        if (!ignore) {
          setLatestBlogs([]);
          setAgents([]);
          setBlogAssetBase("");
        }
      }
    };

    loadDynamicSections();
    return () => {
      ignore = true;
    };
  }, [locale]);

  const serviceIcons = useMemo(
    () => ["flaticon-house", "flaticon-mortgage", "flaticon-operator", "flaticon-house-1", "flaticon-building"],
    []
  );

  useEffect(() => {
    const root = testimonialSliderRef.current;
    const $ = window.jQuery || window.$;
    if (!root || localizedTestimonials.length === 0) return;

    const $slider = $ ? $(root) : null;
    if ($slider?.hasClass("slick-initialized")) {
      try {
        $slider.slick("unslick");
      } catch {
        /* ignore stale slick instance */
      }
    }

    root.innerHTML = localizedTestimonials
      .map(
        (item, index) => `
          <div class="col-lg-4 col-md-6 col-12" data-index="${index}">
            <div class="ltn__testimonial-item ltn__testimonial-item-7">
              <div class="ltn__testimoni-info">
                <p>
                  <i class="flaticon-left-quote-1"></i>
                  ${escapeHtml(item.quote)}
                </p>
                <div class="ltn__testimoni-info-inner">
                  <div class="ltn__testimoni-img">
                    <img src="${testimonialIconImage}" alt="${escapeHtml(item.name)}" />
                  </div>
                  <div class="ltn__testimoni-name-designation">
                    <h5>${escapeHtml(item.name)}</h5>
                    <label>${escapeHtml(item.role)}</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `
      )
      .join("");

    if (!$slider || typeof $.fn?.slick !== "function") return undefined;

    $slider.slick({
      accessibility: false,
      autoplay: true,
      autoplaySpeed: 3500,
      arrows: true,
      dots: false,
      infinite: true,
      rtl: locale === "ar",
      speed: 300,
      slidesToShow: 3,
      slidesToScroll: 1,
      prevArrow: '<a class="slick-prev"><i class="fas fa-arrow-left" alt="Arrow Icon"></i></a>',
      nextArrow: '<a class="slick-next"><i class="fas fa-arrow-right" alt="Arrow Icon"></i></a>',
      responsive: [
        {
          breakpoint: 1200,
          settings: { arrows: false, dots: true, slidesToShow: 2, slidesToScroll: 1 },
        },
        {
          breakpoint: 768,
          settings: { arrows: false, dots: true, slidesToShow: 1, slidesToScroll: 1 },
        },
      ],
    });

    return () => {
      if ($slider.hasClass("slick-initialized")) {
        try {
          $slider.slick("unslick");
        } catch {
          /* ignore teardown issues */
        }
      }
      root.innerHTML = "";
    };
  }, [localizedTestimonials, locale]);

  useEffect(() => {
    const root = newsSliderRef.current;
    const $ = window.jQuery || window.$;
    if (!root || !$ || typeof $.fn?.slick !== "function" || localizedBlogs.length === 0) return;

    root.innerHTML = localizedBlogs
      .slice(0, 5)
      .map((blog) => {
        const slug = blog.slug || blog.id || "";
        const href = `/blogs-details/${escapeHtml(slug)}`;
        const image = escapeHtml(resolveBlogImage(blog.coverImageUrl, blogAssetBase));
        const title = escapeHtml(blog.title || "Blog");
        const author = escapeHtml(blog.author?.fullName || "Admin");
        const tag = escapeHtml(blog.tags?.[0]?.name || localizedUi.newsTag);
        const date = escapeHtml(formatBlogDate(blog.publishedAt || blog.createdAt));

        return `
          <div class="col-lg-12">
            <div class="ltn__blog-item ltn__blog-item-3">
              <div class="ltn__blog-img">
                <a href="${href}">
                  <img class="qtech-blog-card-image" src="${image}" alt="${title}" onerror="this.src='${fallbackBlogImage}'" />
                </a>
              </div>
              <div class="ltn__blog-brief">
                <div class="ltn__blog-meta">
                  <ul>
                    <li class="ltn__blog-author">
                      <a href="#!">
                        <i class="far fa-user"></i>
                        ${escapeHtml(localizedUi.by)}: ${author}
                      </a>
                    </li>
                    <li class="ltn__blog-tags">
                      <a href="#!">
                        <i class="fas fa-tags"></i>
                        ${tag}
                      </a>
                    </li>
                  </ul>
                </div>
                <h3 class="ltn__blog-title animated fadeIn">
                  <a href="${href}">${title}</a>
                </h3>
                <div class="ltn__blog-meta-btn">
                  <div class="ltn__blog-meta">
                    <ul>
                      <li class="ltn__blog-date">
                        <i class="far fa-calendar-alt"></i>
                        ${date}
                      </li>
                    </ul>
                  </div>
                  <div class="ltn__blog-btn">
                    <a href="${href}">${escapeHtml(localizedUi.readMore)}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        `;
      })
      .join("");

    const $slider = $(root);
    if ($slider.hasClass("slick-initialized")) {
      try {
        $slider.slick("unslick");
      } catch {
        /* ignore stale slick instance */
      }
    }

    $slider.slick({
      accessibility: false,
      autoplay: true,
      autoplaySpeed: 3500,
      arrows: true,
      dots: false,
      infinite: localizedBlogs.length > 3,
      rtl: locale === "ar",
      speed: 300,
      slidesToShow: 3,
      slidesToScroll: 1,
      prevArrow: '<a class="slick-prev"><i class="fas fa-arrow-left" alt="Arrow Icon"></i></a>',
      nextArrow: '<a class="slick-next"><i class="fas fa-arrow-right" alt="Arrow Icon"></i></a>',
      responsive: [
        {
          breakpoint: 1200,
          settings: { arrows: false, dots: true, slidesToShow: 2, slidesToScroll: 1 },
        },
        {
          breakpoint: 768,
          settings: { arrows: false, dots: true, slidesToShow: 1, slidesToScroll: 1 },
        },
      ],
    });

    return () => {
      if ($slider.hasClass("slick-initialized")) {
        try {
          $slider.slick("unslick");
        } catch {
          /* ignore teardown issues */
        }
      }
      root.innerHTML = "";
    };
  }, [blogAssetBase, localizedBlogs, localizedUi, locale]);

  return (
    <div>
      <Seo
        title="About QTECH | Real Estate Services Company in Saudi Arabia"
        titleAr="عن شركة تقنيات الجودة | شركة عقارية في الخبر"
        description="Learn about QTECH, a Saudi real estate services company providing property management, brokerage, marketing, maintenance and operations, and property development solutions."
        descriptionAr="تعرف على شركة تقنيات الجودة، شركة عقارية في الخبر تقدم خدمات عقارية تشمل إدارة الأملاك، الوساطة العقارية، التسويق العقاري، صيانة المباني، والتطوير العقاري."
        keywords={mergeSeoKeywords(
          "about QTECH, real estate company Saudi Arabia, real estate services Al Khobar",
          "real estate investment company Saudi Arabia, investment property services Al Khobar, real estate investment support Saudi Arabia"
        )}
        keywordsAr={mergeSeoKeywords(
          "شركة تقنيات الجودة، شركة عقارية، شركة عقارية في الخبر، خدمات عقارية في الخبر، خدمات عقارية في السعودية، إدارة أملاك، وساطة عقارية، تسويق عقاري، صيانة مباني، تطوير عقاري",
          "شركة استثمار عقاري، استثمار عقاري في السعودية، استثمار عقاري في الخبر، خدمات عقارية للمستثمرين"
        )}
        path="/about"
        image="/img/service/Gemini_Generated_Image_lchs6blchs6blchs.png"
      />
      <PageTitle titleKey="pages.about.title" currentPageKey="pages.about.title" bgImage="/img/bg/14.jpg" />

      <div className="ltn__about-us-area pt-120--- pb-90 mt--30">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 align-self-center">
              <div className="about-us-img-wrap about-img-left about-page-image-wrap">
                <img className="about-page-main-image" src="img/service/Gemini_Generated_Image_lchs6blchs6blchs.png" alt="About Us" />
                <div className="about-us-img-info about-us-img-info-2 about-us-img-info-3 about-page-video-card">
                  <div className="ltn__video-img ltn__animation-pulse1 about-page-video-thumb">
                    <img src="img/service/gallery_8.jpg" alt="Video thumbnail" />
                    <a
                      className="ltn__video-icon-2 ltn__video-icon-2-border---"
                      href="https://www.tiktok.com/@qualitytechnologies/video/7631603650288192788?is_from_webapp=1&sender_device=pc&web_id=7595149882567263751"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fa fa-play" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 align-self-center">
              <div className="about-us-info-wrap">
                <div className="section-title-area ltn__section-title-2--- mb-20">
                  <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">{localizedUi.aboutUs}</h6>
                  <h1 className="section-title">
                    {localizedUi.heading}
                    <span>.</span>
                  </h1>
                  <p>{localizedUi.paragraph1}</p>
                  <p className="mt-4">{localizedUi.paragraph2}</p>
                </div>
                <ul className="ltn__list-item-half clearfix">
                  {(servicesCopy.cards || []).slice(0, 5).map((card, index) => (
                    <li key={card.href}>
                      <i className={serviceIcons[index] || "flaticon-house"} />
                      {card.title}
                    </li>
                  ))}
                </ul>
                <div className="ltn__callout bg-overlay-theme-05 mt-30">
                  <p>
                    {localizedUi.trustCallout}
                    <br />
                    {localizedUi.trustCallout2}
                  </p>
                </div>
                <div className="btn-wrapper animated">
                  <a href="/services" className="theme-btn-1 btn btn-effect-1">
                    {localizedUi.servicesButton}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ltn__feature-area section-bg-1 pt-120 pb-90 mb-120---">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title-area ltn__section-title-2--- text-center">
                <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">{localizedUi.coreSubtitle}</h6>
                <h1 className="section-title">{localizedUi.coreTitle}</h1>
              </div>
            </div>
          </div>
          <div className="row justify-content-center gy-4">
            {(servicesCopy.cards || []).map((card, index) => (
              <div className="col-lg-4 col-sm-6 col-12 d-flex" key={card.href}>
                <div className="ltn__feature-item ltn__feature-item-6 text-center bg-white box-shadow-1 h-100 w-100 d-flex flex-column">
                  <div className="ltn__feature-icon">
                    <span>
                      <i className={serviceIcons[index] || "flaticon-house"} />
                    </span>
                  </div>
                  <div className="ltn__feature-info">
                    <h3 className="animated fadeIn">
                      <Link to={card.href}>
                        {card.title}
                      </Link>
                    </h3>
                    <p>{card.body}</p>
                    <Link className="ltn__service-btn" to="/services">
                      {localizedUi.servicesButton} <i className="flaticon-right-arrow" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="ltn__team-area pt-115 pb-90">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title-area ltn__section-title-2--- text-center">
                <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">{localizedUi.team}</h6>
                <h1 className="section-title">{localizedUi.propertyAgents}</h1>
              </div>
            </div>
          </div>
          <div className="row justify-content-center">
            {localizedAgents.map((agent, index) => (
              <div className="col-lg-4 col-sm-6" key={agent.id || agent.email || index}>
                <div className="ltn__team-item ltn__team-item-3--- about-agent-card">
                  <div className="team-img about-agent-image-wrap">
                    <img
                      className="about-agent-image"
                      src={resolveAgentImage(agent.imageUrl) || fallbackAgentImages[index % fallbackAgentImages.length]}
                      alt={agent.name || "Agent"}
                    />
                  </div>
                  <div className="team-info">
                    <h4>
                      <a href={`mailto:${agent.email}`}>{agent.name || "Property Agent"}</a>
                    </h4>
                    <h6 className="ltn__secondary-color">{agent.companyName || agent.licenseNumber || "Property Agent"}</h6>
                    <div className="ltn__social-media">
                      <ul>
                        {agent.email ? (
                          <li>
                            <a href={`mailto:${agent.email}`} aria-label={`Email ${agent.name || "agent"}`}>
                              <i className="far fa-envelope" />
                            </a>
                          </li>
                        ) : null}
                        {agent.phone ? (
                          <li>
                            <a href={`tel:${agent.phone}`} aria-label={`Call ${agent.name || "agent"}`}>
                              <i className="fas fa-phone-alt" />
                            </a>
                          </li>
                        ) : null}
                        {agent.phone ? (
                          <li>
                            <a
                              href={`https://wa.me/${String(agent.phone).replace(/[^\d]/g, "")}`}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`WhatsApp ${agent.name || "agent"}`}
                            >
                              <i className="fab fa-whatsapp" />
                            </a>
                          </li>
                        ) : (
                          <li>
                            <a href="/contact" aria-label={localizedUi.contactQtech}>
                              <i className="fas fa-user-tie" />
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {localizedAgents.length === 0 ? (
              <div className="col-12 text-center">
                <p>{localizedUi.noAgents}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="ltn__testimonial-area section-bg-1--- bg-image-top pt-120 pb-70" data-bs-bg="img/bg/20.jpg" style={{ backgroundImage: 'url("img/bg/20.jpg")' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title-area ltn__section-title-2--- text-center">
                <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">{localizedUi.testimonialSubtitle}</h6>
                <h1 className="section-title">{localizedUi.testimonialTitle}</h1>
              </div>
            </div>
          </div>
          <div ref={testimonialSliderRef} className="row qtech-about-testimonial-slider slick-arrow-1" />
        </div>
      </div>

      <div className="ltn__blog-area pt-115--- pb-70">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="section-title-area ltn__section-title-2--- text-center">
                <h6 className="section-subtitle section-subtitle-2 ltn__secondary-color">{localizedUi.newsSubtitle}</h6>
                <h1 className="section-title">{localizedUi.newsTitle}</h1>
              </div>
            </div>
          </div>
          {localizedBlogs.length > 0 ? (
            <div ref={newsSliderRef} className="row ltn__blog-slider-one-active slick-arrow-1 ltn__blog-item-3-normal" />
          ) : (
            <div className="row">
              <div className="col-12 text-center">
                <p>{localizedUi.noBlogs}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default About;